import { ReactElement, Suspense, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Route, withRouter } from 'react-router-dom';
import cc from 'classcat';
import { ColSizes } from 'utils/constants/app/ui';
import { DESKTOP_RIGHT_WRAPPER_Z_INDEX } from 'utils/constants/sportsbook/generic';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { mountPoint } from 'utils/generic/route-manipulation';
import { routePathWithMockedDataCheck } from 'utils/helpers';
import { isMobile } from 'utils/is-mobile';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { ESportsbookPageTypes } from 'interfaces/sportsbook-configs';
import { FirstColumnContent } from './FirstColumnContent';
import { SecondColumnContent } from './SecondColumnContent';
import { Statscore } from 'elements/Statscore';
import { NoItem } from 'components/Shared/NoItem';
import { DesktopRight } from 'newcomponents/Shared/DesktopRight';
import { HooryAiCardLazyWrapper } from 'newcomponents/Shared/generic/HooryAI/HooryAICard/HooryAiCardLazyWrapper';
import { HooryAICompact } from 'newcomponents/Shared/generic/HooryAI/HooryAICompact';
import { Col } from 'newcomponents/UI/Col';
import { LivePrematchTabsSkeleton } from 'newcomponents/UI/LivePrematchTabsSkeleton';
import { MenuUnderlineSkeleton } from 'newcomponents/UI/Menu/layouts/underline/skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { useSbSportsbookRouteMatch } from 'newhooks/sportsbook/useSbSportsbookRouteMatch';
import { setShowNoItemWrapper } from 'store/actions/new-sportsbook';
import {
  getShowNoItemWrapper,
  getSportsData
} from 'store/selectors/new-sportsbook';
import '../../index.less';
import './index.less';

const { LivePrematchTabs } = lazy(
  () =>
    import(
      /* webpackChunkName: "live-prematch-tabs" */ 'components/LivePrematchSwitcher/livePrematchTabs'
    )
);

const { SportsbookSportsMenu } = lazy(
  () =>
    import(
      /* webpackChunkName: "sportsbook-sports-menu" */ 'newelements/Sportsbook/__partials/SportsbookSportsMenu'
    )
);

const { DesktopPrematchSportsMenu } = lazy(
  () =>
    import(
      /* webpackChunkName: "sports-menu-prematch-desktop" */ 'newelements/Sportsbook/__partials/SportsbookSportsMenu/DesktopPrematchSportsMenu'
    )
);

export type TDesktopSportsbookProps = RouteComponentProps & {
  configs: {
    fixedScroll: boolean;
    fit: 'cover' | 'contain';
    type: 'live' | 'prematch';
    category: 'sport' | 'esport';
    spbLayoutType?: number;
    sportType: string;
    favoriteTeam?: boolean;
  };
};

export const INFINITE_SCROLL_ID = 'InfiniteScrollId';

const _DesktopSportsbookClassic = ({
  location
}: TDesktopSportsbookProps): ReactElement => {
  const dispatch = useDispatch();
  const configs = useSportsbookConfigs();
  const isLive = configs.pageType === ESportsbookPageTypes.live;

  const generatedSbPath = useSbSportsbookRouteMatch(
    '/:sport?/:region?/:competition?/:game?'
  );

  const routeParams = useSbRouteMatch(
    `${mountPoint(location.pathname)}/:sport?/:region?/:competition?`
  );

  const { t } = useTranslation();

  const sportsData = useSelector(getSportsData)?.[configs.type];

  const showNoItemWrapper = useSelector(getShowNoItemWrapper);

  const isCoupon = routeParams?.params?.sport === ENotRealSports.coupon;
  const showSideBar =
    SpringConfigs.LEFT_SIDE_BAR &&
    (SpringConfigs.LEFT_SIDE_BAR_SPORTS || SpringConfigs.LEFT_SIDE_BAR_CASINO);

  useLayoutEffect(() => {
    dispatch(
      setShowNoItemWrapper({
        game: false,
        competitions: false,
        regions: false
      })
    );
  }, [location.pathname]);

  const NoItemContainer =
    showNoItemWrapper.competitions && showNoItemWrapper.game;

  const isEmptySportData = sportsData === undefined;

  return (
    <div
      className={cc([
        'desktopSportsbookClassic',
        { 'desktopSportsbookClassic--live': isLive }
      ])}
    >
      <Route path={routePathWithMockedDataCheck(generatedSbPath)}>
        <Col
          className={cc([
            {
              SBCol: configs.fit === 'cover',
              'prematch__type-page':
                configs.pageType === ESportsbookPageTypes.prematch,
              SBCol__fixedScroll: configs.fixedScroll
            }
          ])}
          id="prematch-left-sidebar-wrapper"
          data-popover-parent
          xs={ColSizes.SIZE_5}
        >
          <HooryAICompact />
          {SpringConfigs.LIVE_PREMATCH_SWITCHER && (
            <Suspense
              fallback={
                <LivePrematchTabsSkeleton
                  fullWidth
                  activeItem={configs.type}
                  className="live-prematch-switcher"
                />
              }
            >
              <LivePrematchTabs />
            </Suspense>
          )}

          <FirstColumnContent isLive={isLive} configs={configs} />
        </Col>
        <Col
          className={cc([{ SBCol: configs.fit === 'cover' }])}
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden'
          }}
          xs={ColSizes.SIZE_14}
        >
          {!isMobile() && <Statscore />}

          <Suspense
            fallback={
              !+SpringConfigs.LEFT_SIDE_BAR_SPORTS ||
              !+SpringConfigs.LEFT_SIDE_BAR ||
              isLive ? (
                <MenuUnderlineSkeleton />
              ) : null
            }
          >
            {isEmptySportData ? null : isLive ? (
              <SportsbookSportsMenu />
            ) : (
              <DesktopPrematchSportsMenu />
            )}
          </Suspense>

          {NoItemContainer || isEmptySportData ? (
            <NoItem
              icon={isCoupon ? ENotRealSports.coupon : undefined}
              title={t('sportsbook.noGame')}
              text={t('sportsbook.noGameText')}
              className={cc([
                {
                  notItem__wrapper: showSideBar || isEmptySportData
                }
              ])}
            />
          ) : (
            <SecondColumnContent
              category={routeParams?.params?.category}
              isLive={isLive}
              configs={configs}
              paramsSport={routeParams?.params?.sport}
            />
          )}
        </Col>
        <Col
          className={cc([{ SBCol: configs.fit === 'cover' }])}
          xs={ColSizes.SIZE_5}
          style={{
            zIndex: DESKTOP_RIGHT_WRAPPER_Z_INDEX,
            display: SpringConfigs.HOORY_ENABLED_SPORTSBOOK_BETSLIP
              ? 'flex'
              : 'block'
          }}
        >
          <DesktopRight isLive={isLive} />
          <HooryAiCardLazyWrapper />
        </Col>
      </Route>
    </div>
  );
};

export const DesktopSportsbookClassic: ReturnType<typeof withRouter> | any =
  withRouter(_DesktopSportsbookClassic);
